import { type Ref, ref } from "vue";

import { defineStore } from "pinia";

import customWebhookService from "@/services/modules/customWebhook";

import type { TemplateViewParams } from "@/models/clients";
import type {
  IPaginatedResponse,
  IResponseLinks,
  IResponseMeta,
} from "@/models/common";
import type { CustomWebhook } from "@/models/customWebhook";

interface UseWebhooksState {
  customWebhooks: IPaginatedResponse<CustomWebhook>;
  loadingCustomWebhooks: boolean;
  activeCustomWebhook: CustomWebhook | null;
}

interface CustomWebhookStoreReturn {
  customWebhooks: Ref<IPaginatedResponse<CustomWebhook>>;
  loadingCustomWebhooks: Ref<boolean>;
  activeCustomWebhook: Ref<CustomWebhook | null>;
  getCustomWebhooks: (
    payload: TemplateViewParams
  ) => Promise<IPaginatedResponse<CustomWebhook>>;
  getCustomWebhook: (id: string) => Promise<void>;
  createCustomWebhook: (values: {
    payload: Partial<CustomWebhook>;
  }) => Promise<void>;
  updateCustomWebhook: (values: {
    payload: Partial<CustomWebhook>;
  }) => Promise<void>;
  unsetCustomWebhooks: () => void;
}

const getDefaultState = (): UseWebhooksState => ({
  customWebhooks: {
    data: [],
    meta: {} as IResponseMeta,
    links: {} as IResponseLinks,
  },
  loadingCustomWebhooks: false,
  activeCustomWebhook: null,
});

export const useCustomWebhookStore = defineStore(
  "customWebhook",
  (): CustomWebhookStoreReturn => {
    const customWebhooks = ref<IPaginatedResponse<CustomWebhook>>(
      getDefaultState().customWebhooks
    );
    const loadingCustomWebhooks = ref(getDefaultState().loadingCustomWebhooks);
    const activeCustomWebhook = ref<CustomWebhook | null>(
      getDefaultState().activeCustomWebhook
    );
    const getCustomWebhooks = async (payload: TemplateViewParams) => {
      loadingCustomWebhooks.value = true;
      const dataPayload: TemplateViewParams = {
        page: payload.page ?? 1,
        per_page: payload.per_page ?? 100,
      };

      if (payload.search) {
        dataPayload.search = payload.search;
      }
      if (payload.client_id) {
        dataPayload.client_id = payload.client_id;
      }
      const data = await customWebhookService.getCustomWebhooks({
        payload: dataPayload,
      });
      customWebhooks.value = data;
      loadingCustomWebhooks.value = false;
      return data;
    };

    const getCustomWebhook = async (id: string) => {
      const data = await customWebhookService.getCustomWebhook(id);
      activeCustomWebhook.value = data;
    };
    const updateCustomWebhook = async (values: {
      payload: Partial<CustomWebhook>;
    }) => {
      const data = await customWebhookService.updateCustomWebhook(values);
      if (data) {
        activeCustomWebhook.value = data;
      }
    };
    const createCustomWebhook = async (values: {
      payload: Partial<CustomWebhook>;
    }) => {
      const data = await customWebhookService.createCustomWebhook(values);
      activeCustomWebhook.value = data;
    };
    const unsetCustomWebhooks = () => {
      customWebhooks.value = getDefaultState().customWebhooks;
    };

    return {
      customWebhooks,
      loadingCustomWebhooks,
      activeCustomWebhook,
      getCustomWebhooks,
      getCustomWebhook,
      createCustomWebhook,
      updateCustomWebhook,
      unsetCustomWebhooks,
    };
  }
);
